import { useState } from 'react';
import CalEmbed from '../components/CalEmbed';
import {
  INTEREST_OPTIONS,
  isQualifiedLead,
  type Interest,
} from '../lib/qualification';
import { useDocumentMeta } from '../lib/useDocumentMeta';

type Step = 'form' | 'calendar' | 'not-fit';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseGuestEmails(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
}

export default function Book() {
  useDocumentMeta({
    title: 'Book a Diagnostic — Samia Systems',
    description:
      'Complete a short qualification form, then book a 30-minute consultation with the Samia Systems team.',
  });

  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [interest, setInterest] = useState<Interest>(
    INTEREST_OPTIONS[0].value
  );
  const [projectBudgetUsd, setProjectBudgetUsd] = useState('');
  const [monthlyRevenueUsd, setMonthlyRevenueUsd] = useState('');
  const [notes, setNotes] = useState('');
  const [guests, setGuests] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guestEmails = parseGuestEmails(guests);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const invalidGuest = guestEmails.find(
      (guestEmail) => !EMAIL_RE.test(guestEmail)
    );
    if (invalidGuest) {
      setError(`Please check the guest email: ${invalidGuest}`);
      return;
    }

    const qualification = {
      interest,
      projectBudgetUsd: Number(projectBudgetUsd),
      monthlyRevenueUsd: Number(monthlyRevenueUsd),
    };
    const localQualification = isQualifiedLead(qualification);

    setSubmitting(true);
    try {
      const response = await fetch('/api/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          interest,
          projectBudgetUsd: qualification.projectBudgetUsd,
          monthlyRevenueUsd: qualification.monthlyRevenueUsd,
          notes,
          guests: guestEmails,
        }),
      });

      if (!response.ok) {
        const result = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        throw new Error(result?.error || 'We could not save your answers.');
      }

      // A successful response confirms that Notion recorded the answers.
      // Route from the rules bundled with this page so a stale edge function
      // cannot send a visitor to the wrong next step.
      setStep(localQualification ? 'calendar' : 'not-fit');
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'We could not save your answers. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="grain relative bg-black px-6 pb-12 pt-40">
        <div className="container-x">
          <p className="label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Consultation
          </p>
          <h1 className="max-w-4xl font-display text-hero font-light text-white text-balance">
            The right next step,{' '}
            <span className="italic text-gold">without the guesswork.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-sans text-base text-white/85">
            {step === 'form' &&
              'Answer a few questions so we can direct you to the most useful next step.'}
            {step === 'calendar' &&
              'You are a fit for a consultation. Choose a time below — your answers are already attached.'}
            {step === 'not-fit' && 'Thank you for sharing your answers.'}
          </p>
        </div>
      </section>

      <section className="bg-black pb-32">
        <div className="container-x">
          <div className="mx-auto max-w-4xl">
            {step === 'form' && (
              <div className="grain rounded-5xl border border-white/10 bg-charcoal p-6 md:p-10">
                <p className="label mb-2">Qualification · Step 1 of 2</p>
                <h2 className="font-display text-2xl font-light text-white">
                  Tell us about the opportunity
                </h2>
                <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5">
                  <div className="grid gap-5 md:grid-cols-2">
                    <Field
                      label="Your name"
                      value={name}
                      onChange={setName}
                      required
                      autoComplete="name"
                    />
                    <Field
                      label="Email address"
                      type="email"
                      value={email}
                      onChange={setEmail}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <fieldset>
                    <legend className="label mb-2 block">
                      What are you interested in? *
                    </legend>
                    <div className="grid gap-2 md:grid-cols-2">
                      {INTEREST_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className={`flex cursor-pointer items-center gap-3 rounded-2xl border bg-black/60 px-4 py-3 font-sans text-sm transition-colors ${
                            interest === option.value
                              ? 'border-gold text-white'
                              : 'border-white/10 text-white/75 hover:border-white/30 hover:text-white/90'
                          }`}
                        >
                          <input
                            type="radio"
                            name="interest"
                            value={option.value}
                            checked={interest === option.value}
                            onChange={() => setInterest(option.value)}
                            className="accent-gold"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="grid gap-5 md:grid-cols-2">
                    <NumberField
                      label="Project budget (USD)"
                      value={projectBudgetUsd}
                      onChange={setProjectBudgetUsd}
                      placeholder="0"
                      required
                    />
                    <NumberField
                      label="What is your monthly revenue?"
                      value={monthlyRevenueUsd}
                      onChange={setMonthlyRevenueUsd}
                      placeholder="0"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="consultation-notes" className="label mb-2 block">
                      Additional notes
                    </label>
                    <textarea
                      id="consultation-notes"
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      rows={4}
                      maxLength={1000}
                      placeholder="Please share anything that will help prepare for our meeting."
                      className="w-full resize-y rounded-2xl border border-white/10 bg-black/60 px-4 py-3 font-sans text-sm text-white placeholder-stone focus:border-gold focus:outline-none"
                    />
                  </div>

                  <Field
                    label="Add guests (optional)"
                    type="text"
                    value={guests}
                    onChange={setGuests}
                    placeholder="guest@example.com, colleague@example.com"
                    autoComplete="off"
                    hint="Separate multiple email addresses with commas."
                  />

                  {error && (
                    <p
                      role="alert"
                      aria-live="polite"
                      className="font-sans text-caption text-gold"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-gold bg-gold px-7 py-3.5 font-sans text-[0.7rem] uppercase tracking-label text-black transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? 'Checking…' : 'Find my next step'} →
                  </button>
                </form>
              </div>
            )}

            {step === 'calendar' && (
              <CalEmbed
                prefill={{
                  name,
                  email,
                  interest,
                  projectBudgetUsd,
                  monthlyRevenueUsd,
                  notes,
                  guests: guestEmails,
                }}
              />
            )}

            {step === 'not-fit' && (
              <div
                className="grain rounded-5xl border border-white/10 bg-charcoal p-10 text-center md:p-16"
                role="status"
                aria-live="polite"
              >
                <p className="label mb-4">Thank you</p>
                <h2 className="font-display text-4xl font-light text-white">
                  Not the right fit right now.
                </h2>
                <p className="mx-auto mt-5 max-w-xl font-sans text-body text-white/75">
                  Based on your answers, a consultation is not the right next step
                  at the moment. Your answers are saved, so if your situation
                  changes, we will already have the context.
                </p>
              </div>
            )}
          </div>

          {step === 'calendar' && (
            <p className="mt-8 text-center font-mono text-[0.65rem] uppercase tracking-label text-stone">
              30 minutes · Cal Video · Europe/London
            </p>
          )}
        </div>
      </section>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required,
  autoComplete,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="label mb-2 block">
        {label}
        {required ? ' *' : ''}
        <input
          type={type}
          value={value}
          required={required}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="mt-2 w-full rounded-2xl border border-white/10 bg-black/60 px-4 py-3 font-sans text-sm normal-case tracking-normal text-white placeholder-stone focus:border-gold focus:outline-none"
        />
      </label>
      {hint && (
        <p className="mt-2 font-sans text-xs normal-case tracking-normal text-stone">
          {hint}
        </p>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="label mb-2 block">
        {label}
        {required ? ' *' : ''}
        <span className="relative mt-2 block">
          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center font-sans text-sm text-gold">
            $
          </span>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            value={value}
            required={required}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            className="w-full rounded-2xl border border-white/10 bg-black/60 py-3 pl-8 pr-4 font-sans text-sm normal-case tracking-normal text-white placeholder-stone focus:border-gold focus:outline-none"
          />
        </span>
      </label>
    </div>
  );
}
